import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, BarChart3, Upload, Users, ChevronDown, Wallet, Heart, Target, Activity, Menu, Pill, LineChart, Sparkles, Gift } from "lucide-react";
import { useState } from "react";
import { useAuth, getDefaultRouteForRole } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_amtm-animal-care/artifacts/6ng7d8bg_amtm_logo_trim.png";

const ADMIN_GROUPS = [
  {
    key: "fundraising",
    label: "Fundraising",
    icon: Wallet,
    links: [
      { to: "/admin/donations", label: "Donations", icon: Wallet },
      { to: "/admin/guardians", label: "Guardians", icon: Heart },
      { to: "/admin/fundraisers", label: "Fundraisers", icon: Target },
      { to: "/admin/diwali", label: "Diwali", icon: Gift },
    ],
  },
  {
    key: "insights",
    label: "Insights",
    icon: BarChart3,
    links: [
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/admin/performance", label: "Performance", icon: LineChart },
      { to: "/admin/diagnostics", label: "Diagnostics", icon: Activity },
    ],
  },
  {
    key: "manage",
    label: "Manage",
    icon: Users,
    links: [
      { to: "/admin/users", label: "Staff", icon: Users },
      { to: "/admin/import", label: "Import CSV", icon: Upload },
      { to: "/admin/inventory", label: "Medical Inventory", icon: Pill },
      { to: "/admin/campaigns", label: "Campaigns", icon: Sparkles },
    ],
  },
];

const INVENTORY_MANAGER_GROUP = {
  key: "medical-inventory",
  label: "Medical Inventory",
  icon: Pill,
  links: [
    { to: "/admin/inventory/usage", label: "Daily Usage Entry", icon: Pill },
    { to: "/admin/inventory", label: "Inventory List", icon: Pill },
    { to: "/admin/inventory/receive", label: "Receive Stock", icon: Pill },
    { to: "/admin/inventory/reports", label: "Reports", icon: Pill },
  ],
};

const STAFF_INVENTORY_LINK = { to: "/admin/inventory/usage", label: "Medical Inventory", icon: Pill };

export default function StaffHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isGroupActive = (group) => group.links.some((l) => location.pathname === l.to);
  const homeRoute = getDefaultRouteForRole(user?.role);
  const activeGroup =
    user?.role === "admin"
      ? ADMIN_GROUPS.find((g) => isGroupActive(g))
      : user?.role === "inventory_manager" && isGroupActive(INVENTORY_MANAGER_GROUP)
      ? INVENTORY_MANAGER_GROUP
      : null;

  return (
    <header className="sticky top-0 z-50 bg-amtm-surface border-b border-amtm-border" data-testid="staff-header">
      <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Link to={homeRoute} className="flex items-center gap-2" data-testid="staff-header-logo-link">
          <img src={LOGO_URL} alt="AMTM" className="h-10 w-auto object-contain" />
          <span className="font-display text-lg hidden sm:inline text-amtm-text">Rescue Hub</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link
            to={homeRoute}
            data-testid="nav-dashboard"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${
              location.pathname === homeRoute ? "bg-amtm-teal/15 text-amtm-teal" : "text-amtm-textMuted hover:bg-amtm-bg"
            }`}
          >
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </Link>
          {user?.role === "admin" &&
            ADMIN_GROUPS.map((group) => (
              <DropdownMenu key={group.key}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid={`nav-group-${group.key}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${
                      isGroupActive(group) ? "bg-amtm-teal/15 text-amtm-teal" : "text-amtm-textMuted hover:bg-amtm-bg"
                    }`}
                  >
                    <group.icon className="h-4 w-4" /> {group.label}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {group.links.map((l) => (
                    <DropdownMenuItem key={l.to} asChild data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <Link
                        to={l.to}
                        className={`flex items-center gap-2 w-full ${location.pathname === l.to ? "text-amtm-teal" : ""}`}
                      >
                        <l.icon className="h-4 w-4" /> {l.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
          {user?.role === "inventory_manager" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="nav-group-medical-inventory"
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${
                    isGroupActive(INVENTORY_MANAGER_GROUP) ? "bg-amtm-teal/15 text-amtm-teal" : "text-amtm-textMuted hover:bg-amtm-bg"
                  }`}
                >
                  <Pill className="h-4 w-4" /> Medical Inventory
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {INVENTORY_MANAGER_GROUP.links.map((l) => (
                  <DropdownMenuItem key={l.to} asChild data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <Link to={l.to} className={`flex items-center gap-2 w-full ${location.pathname === l.to ? "text-amtm-teal" : ""}`}>
                      <l.icon className="h-4 w-4" /> {l.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {user?.role === "staff" && (
            <Link
              to={STAFF_INVENTORY_LINK.to}
              data-testid="nav-medical-inventory"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${
                location.pathname === STAFF_INVENTORY_LINK.to ? "bg-amtm-teal/15 text-amtm-teal" : "text-amtm-textMuted hover:bg-amtm-bg"
              }`}
            >
              <Pill className="h-4 w-4" /> {STAFF_INVENTORY_LINK.label}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-sm text-amtm-textMuted" data-testid="staff-header-username">
            {user?.name} ({user?.role})
          </span>
          <button
            type="button"
            className="md:hidden p-2 text-amtm-textMuted hover:bg-amtm-bg rounded-lg"
            data-testid="mobile-menu-trigger"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="staff-header-user-menu-button">
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout} data-testid="staff-header-logout-button">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-[85%] sm:max-w-sm overflow-y-auto" data-testid="mobile-nav-sheet">
          <SheetHeader>
            <SheetTitle>Menu</SheetTitle>
          </SheetHeader>
          <nav className="mt-4 flex flex-col">
            <Link
              to={homeRoute}
              data-testid="mobile-nav-dashboard"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-2 px-2 py-3 rounded-lg text-sm font-medium border-b border-amtm-border ${
                location.pathname === homeRoute ? "text-amtm-teal" : "text-amtm-textMuted"
              }`}
            >
              <LayoutDashboard className="h-4 w-4" /> Dashboard
            </Link>
            {user?.role === "admin" && (
              <Accordion type="single" collapsible defaultValue={activeGroup?.key} className="w-full">
                {ADMIN_GROUPS.map((group) => (
                  <AccordionItem key={group.key} value={group.key}>
                    <AccordionTrigger
                      data-testid={`mobile-nav-group-${group.key}`}
                      className={`px-2 ${isGroupActive(group) ? "text-amtm-teal" : "text-amtm-text"}`}
                    >
                      <span className="flex items-center gap-2">
                        <group.icon className="h-4 w-4" /> {group.label}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-2">
                      <div className="flex flex-col gap-1 pl-4">
                        {group.links.map((l) => (
                          <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-2 py-2 text-sm font-medium ${
                              location.pathname === l.to ? "text-amtm-teal" : "text-amtm-textMuted"
                            }`}
                          >
                            <l.icon className="h-4 w-4" /> {l.label}
                          </Link>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
            {user?.role === "inventory_manager" && (
              <Accordion type="single" collapsible defaultValue={activeGroup?.key} className="w-full">
                <AccordionItem value={INVENTORY_MANAGER_GROUP.key}>
                  <AccordionTrigger
                    data-testid="mobile-nav-group-medical-inventory"
                    className={`px-2 ${isGroupActive(INVENTORY_MANAGER_GROUP) ? "text-amtm-teal" : "text-amtm-text"}`}
                  >
                    <span className="flex items-center gap-2">
                      <Pill className="h-4 w-4" /> Medical Inventory
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-2">
                    <div className="flex flex-col gap-1 pl-4">
                      {INVENTORY_MANAGER_GROUP.links.map((l) => (
                        <Link
                          key={l.to}
                          to={l.to}
                          data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-2 py-2 text-sm font-medium ${
                            location.pathname === l.to ? "text-amtm-teal" : "text-amtm-textMuted"
                          }`}
                        >
                          <l.icon className="h-4 w-4" /> {l.label}
                        </Link>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
            {user?.role === "staff" && (
              <Link
                to={STAFF_INVENTORY_LINK.to}
                data-testid="mobile-nav-medical-inventory"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2 px-2 py-3 rounded-lg text-sm font-medium ${
                  location.pathname === STAFF_INVENTORY_LINK.to ? "text-amtm-teal" : "text-amtm-textMuted"
                }`}
              >
                <Pill className="h-4 w-4" /> {STAFF_INVENTORY_LINK.label}
              </Link>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
}
